// src/components/tools/GloMatchModal.jsx
//
// The disambiguation list for a survey block + section that names more than
// one place.
//
// ── WHY THIS EXISTS AT ALL ─────────────────────────────────────────────────
//
// block+section is unique for 65% of pairs and never opens this. The other 35%
// are genuinely ambiguous: sampling 300 multi-match pairs, 173 (58%) name
// places MORE THAN 50km APART. There is no attribute in the data that resolves
// them -- abstract_num separates every row in only 80% of groups, survey_name
// in 25%, region in 12% -- so the choice is the user's.
//
// ── WHAT EACH COLUMN IS FOR ────────────────────────────────────────────────
//
// County first, because it is the only field a person recognises without
// looking anything up. Then survey and abstract, which are what a land record
// actually cites. Then area and distance, which is what makes one row
// obviously the intended one when the others are hundreds of km away.
//
// The list is short: 93% of ambiguous pairs return 5 rows or fewer.

import { useEffect, useRef } from "react";

function fmtArea(sqMi) {
  if (sqMi === null || sqMi === undefined || Number.isNaN(sqMi)) return "—";
  return sqMi < 10 ? `${sqMi.toFixed(2)} sq mi` : `${Math.round(sqMi)} sq mi`;
}

export default function GloMatchModal({ title, matches, truncated, onPick, onClose }) {
  const boxRef = useRef(null);

  // Escape closes. Without it the only way out is the button, and this opens
  // over a map the user was already navigating.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a survey section"
        className="w-[min(560px,92vw)] max-h-[70vh] flex flex-col rounded-lg border border-slate-700 bg-bg-soft shadow-2xl"
      >
        <div className="px-4 py-3 border-b border-slate-700">
          <div className="text-[12px] text-fg-primary font-medium">{title}</div>
          <div className="text-[10px] text-fg-secondary mt-0.5">
            {matches.length} matches in different places — choose one
            {truncated ? " (showing the nearest 25)" : ""}
          </div>
        </div>

        <div className="overflow-y-auto">
          {matches.map((m) => (
            <button
              key={m.ols_id}
              type="button"
              onClick={() => onPick?.(m)}
              className="w-full text-left px-4 py-2 border-b border-slate-800/70
                         hover:bg-accent-action/10 focus:bg-accent-action/10 focus:outline-none transition"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[11px] text-fg-primary truncate">
                  {m.county ? `${m.county} Co.` : "County unknown"}
                </span>
                <span className="text-[10px] text-fg-secondary tabular-nums flex-shrink-0">
                  {m.distance_km === null ? "" : `${m.distance_km} km`}
                </span>
              </div>
              <div className="text-[10px] text-fg-secondary truncate">
                {m.survey_name || "Survey unknown"}
                {m.abstract_num ? ` · A-${m.abstract_num}` : ""}
                {m.subdivision ? ` · ${m.subdivision}` : ""}
                <span className="tabular-nums"> · {fmtArea(m.area_sq_mi)}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-slate-700 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-[11px] rounded-md border border-slate-700 text-fg-secondary hover:text-fg-primary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
